/**
 * iter-119ae (G4 polish per user feedback) — KPI Drill-Down dialog.
 *
 * Behaviour:
 *   • Shows underlying ROWS, never aggregated (avoids leaking model
 *     field names; matches the iter-119ac redesign).
 *   • For non-SA users, defaults to a HIGH-SIGNAL set of business
 *     columns (top 8) with a "Show all columns" toggle to expand.
 *     SA always sees every column up front (legitimate audit need).
 *   • Dialog itself is capped at `max-w-[95vw]` so it never escapes the
 *     viewport on any device, and the table inside scrolls horizontally
 *     with a sticky bottom scrollbar.
 *
 * RBAC discipline:
 *   • Plumbing columns (tenant_id, source_id, etc.) are stripped
 *     server-side for non-SA users — see /drill-down endpoint.
 *   • Dialog title uses model display name + KPI title; never raw slug.
 */
import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader2, ChevronsRight, ChevronsLeft } from "lucide-react";
import { useAuth } from "@/lib/auth-context";

const _humanize = (k) =>
    k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const _fmtCell = (v) => {
    if (v == null || v === "") return "—";
    if (typeof v === "number") {
        const abs = Math.abs(v);
        if (abs >= 1e9) return (v / 1e9).toFixed(2) + "B";
        if (abs >= 1e6) return (v / 1e6).toFixed(2) + "M";
        if (abs >= 1e3) return (v / 1e3).toFixed(2) + "K";
        return Number.isInteger(v) ? v.toLocaleString() : v.toFixed(2);
    }
    if (typeof v === "boolean") return v ? "Yes" : "No";
    return String(v);
};

// iter-119ae — high-signal business columns shown to non-SA users by
// default. Anything not on this list is collapsed behind "Show all".
// Ordered by display priority so the first 8 visible cols give the
// clearest story without scrolling.
const _HIGH_SIGNAL_COLUMNS = [
    "name", "campaign_name", "account_name", "category", "channel",
    "stage", "status", "owner", "date", "month",
    "spend", "amount", "revenue", "value", "cost",
    "impressions", "clicks", "conversions", "ctr", "cpc", "roas", "cpa",
];

export default function KpiDrillDownDialog({
    open, onOpenChange, kpi, dashboard, filterPayload, decorate, queryUrl,
}) {
    // ISSUE-FIX: Support COUNT_DISTINCT_IF and DISTINCT_COUNT_IF in KpiDrillDownDialog
    function extractConditionFromFormula(formula) {
        if (!formula) return null;
        const mIf = /^\s*(COUNT_DISTINCT_IF|DISTINCT_COUNT_IF|COUNT_IF|SUM_IF|AVG_IF|MIN_IF|MAX_IF)\s*\(\s*(.+?)\s*\)\s*$/i.exec(formula);
        if (mIf) {
            const func = mIf[1].toUpperCase();
            const baseAgg = (func === "COUNT_DISTINCT_IF" || func === "DISTINCT_COUNT_IF") ? "count_distinct" : func.replace("_IF", "").toLowerCase();
            const inner = mIf[2];
            let cond = inner;
            let measure = null;
            if (inner.includes(",")) {
                const parts = inner.split(",");
                const maybeMeasure = parts[parts.length - 1].trim();
                if (/^[a-zA-Z0-9_.]+$/.test(maybeMeasure)) {
                    cond = parts.slice(0, -1).join(",").trim();
                    measure = maybeMeasure;
                }
            }
            return { cond, baseAgg, measure };
        }

        const mWhere = /^\s*(COUNT_DISTINCT|DISTINCT|SUM|AVG|COUNT|MIN|MAX)\s*\(\s*(?:DISTINCT\s+)?(.+?)\s+WHERE\s+(.+?)\s*\)\s*$/i.exec(formula);
        if (mWhere) {
            const baseAgg = mWhere[1].toLowerCase() === "distinct" ? "count_distinct" : mWhere[1].toLowerCase();
            return { cond: mWhere[3].trim(), baseAgg, measure: mWhere[2].trim() };
        }

        const mIfExpr = /^\s*(COUNT_DISTINCT|DISTINCT|SUM|AVG|COUNT|MIN|MAX)\s*\(\s*(?:DISTINCT\s+)?IF\s*\(\s*(.+?)\s*,\s*(.+?)\s*,\s*.+?\s*\)\s*\)\s*$/i.exec(formula);
        if (mIfExpr) {
            const baseAgg = mIfExpr[1].toLowerCase() === "distinct" ? "count_distinct" : mIfExpr[1].toLowerCase();
            return { cond: mIfExpr[2].trim(), baseAgg, measure: mIfExpr[3].trim() };
        }

        const mCase = /^\s*(COUNT_DISTINCT|DISTINCT|SUM|AVG|COUNT|MIN|MAX)\s*\(\s*(?:DISTINCT\s+)?CASE\s+WHEN\s+(.+?)\s+THEN\s+(.+?)\s+(?:ELSE\s+.+?\s+)?END\s*\)\s*$/i.exec(formula);
        if (mCase) {
            const baseAgg = mCase[1].toLowerCase() === "distinct" ? "count_distinct" : mCase[1].toLowerCase();
            return { cond: mCase[2].trim(), baseAgg, measure: mCase[3].trim() };
        }

        return null;
    }
    const { user } = useAuth();
    const isSA = user?.role === "super_admin";
    const [rows, setRows] = useState([]);
    const [columns, setColumns] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [modelName, setModelName] = useState("");
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [error, setError] = useState(null);
    const [showAllCols, setShowAllCols] = useState(false);
    const [offset, setOffset] = useState(0);

    // Parse formula to find the leaf KPI field — used for backend sort.
    // iter-121.51 — Also handle compound formulas like
    // `SUM(m.spend)/SUM(m.conversions)` by picking the FIRST aggregate's
    // leaf column. Without this fallback, compound KPIs (CPA, ROAS, etc.)
    // failed to extract a kpiField → useEffect bailed → drill silently empty.
    const kpiField = useMemo(() => {
        if (!kpi?.formula) return "id";
        const cond = extractConditionFromFormula(kpi.formula);
        if (cond && cond.measure) {
            const leaf = cond.measure.includes(".") ? cond.measure.split(".").pop() : cond.measure;
            return leaf;
        }
        const condRe = /(?:COUNT_DISTINCT_IF|DISTINCT_COUNT_IF|COUNT_IF|SUM_IF|AVG_IF|MIN_IF|MAX_IF)\s*\(\s*(?:[a-zA-Z_][a-zA-Z0-9_]*\.)*([a-zA-Z_][a-zA-Z0-9_]*)\s*(?:==|!=|<=|>=|=|<|>)\s*('[^']*'|"[^"]*"|[A-Za-z0-9._-]+)(?:\s*,\s*(?:[a-zA-Z_][a-zA-Z0-9_]*\.)*([a-zA-Z_][a-zA-Z0-9_]*))?\s*\)/i;
        const cm = condRe.exec(kpi.formula);
        if (cm) return cm[3] || cm[1];
        const re = /(?:COUNT_DISTINCT_IF|DISTINCT_COUNT_IF|SUM_IF|AVG_IF|COUNT_IF|MIN_IF|MAX_IF|SUM|AVG|MIN|MAX|COUNT_DISTINCT|COUNT)\s*\(\s*(?:DISTINCT\s+)?(?:[a-zA-Z_][a-zA-Z0-9_]*\.)*([a-zA-Z_][a-zA-Z0-9_]*)\s*\)/i;
        const m = re.exec(kpi.formula);
        return m ? m[1] : "id";
    }, [kpi?.formula]);

    const decorateRef = React.useRef(decorate);
    useEffect(() => {
        decorateRef.current = decorate;
    }, [decorate]);

    const filterPayloadStr = JSON.stringify(filterPayload);

    useEffect(() => {
        if (!open) return;
        setOffset(0);
        setRows([]);
        setColumns([]);
        setError(null);
        setShowAllCols(false);
    }, [open, kpi?.id, dashboard?.id, filterPayloadStr, kpiField]);

    useEffect(() => {
        if (!open || !kpi?.id || !dashboard?.id) return;
        
        const isLoadMore = offset > 0;
        if (isLoadMore) {
            setLoadingMore(true);
        } else {
            setLoading(true);
        }

        const body = decorateRef.current ? decorateRef.current({}) : {};
        body.kpi_field = kpiField || "id";
        const kpiFilters = kpi?.filters || kpi?.where || (kpi?.config?.filters) || [];
        body.filters = [...(filterPayload || []), ...(Array.isArray(kpiFilters) ? kpiFilters : [])];
        body.formula = kpi?.formula || "";
        body.limit = 50;
        body.offset = offset;

        let active = true;
        api.post(`/dashboards/${dashboard.id}/drill-down`, body)
            .then((r) => {
                if (!active) return;
                if (isLoadMore) {
                    setRows(prev => [...prev, ...(r.data?.rows || [])]);
                } else {
                    setRows(r.data?.rows || []);
                    setColumns(r.data?.columns || []);
                }
                setTotalCount(r.data?.total_count || 0);
                setModelName(r.data?.model_display_name || "");
            })
            .catch((e) => {
                if (!active) return;
                if (!isLoadMore) setError(e?.response?.data?.detail || "Drill-down failed");
            })
            .finally(() => {
                if (!active) return;
                setLoading(false);
                setLoadingMore(false);
            });
            
        return () => { active = false; };
    }, [open, kpi?.id, kpiField, dashboard?.id, filterPayloadStr, offset]);

    // Strip empty-everywhere columns + pin the KPI source column and grouping/breakdown columns first.
    const pinFields = useMemo(() => {
        const out = [];
        if (kpiField) out.push(kpiField);
        
        // Add group_by fields
        const gbs = Array.isArray(kpi?.group_by) ? kpi.group_by : (kpi?.group_by ? [kpi.group_by] : []);
        gbs.forEach((g) => {
            const f = g ? (String(g).includes(".") ? String(g).split(".").pop() : g) : null;
            if (f && !out.includes(f)) out.push(f);
        });

        // Add dimensions fields
        const dims = Array.isArray(kpi?.dimensions) ? kpi.dimensions : (kpi?.dimensions ? [kpi.dimensions] : []);
        dims.forEach((d) => {
            const f = d ? (d.field || d) : null;
            const fName = f ? (String(f).includes(".") ? String(f).split(".").pop() : f) : null;
            if (fName && !out.includes(fName)) out.push(fName);
        });
        
        return out;
    }, [kpi, kpiField]);

    const nonEmptyCols = useMemo(() => {
        if (columns.length === 0) return [];
        const nonEmpty = new Set();
        rows.forEach((r) => {
            columns.forEach((c) => {
                const v = r[c];
                if (v != null && v !== "") nonEmpty.add(c);
            });
        });
        const ordered = columns.filter((c) => nonEmpty.has(c));
        const pins = pinFields.filter((f) => nonEmpty.has(f));
        pins.forEach((f) => {
            const idx = ordered.indexOf(f);
            if (idx > -1) {
                ordered.splice(idx, 1);
            }
        });
        ordered.unshift(...pins);
        return ordered;
    }, [columns, rows, pinFields]);

    // iter-119ae — collapse to high-signal subset by default for non-SA.
    // SA always sees the full set up front. KPI source column is always
    // pinned visible regardless of the high-signal allowlist.
    const visibleColumns = useMemo(() => {
        if (isSA || showAllCols) return nonEmptyCols;
        const high = new Set(_HIGH_SIGNAL_COLUMNS);
        const filtered = nonEmptyCols.filter((c) => high.has(c) || c === kpiField);
        return filtered.length > 0 ? filtered.slice(0, 8) : nonEmptyCols.slice(0, 8);
    }, [nonEmptyCols, isSA, showAllCols, kpiField]);

    const hiddenCount = nonEmptyCols.length - visibleColumns.length;

    const handleScroll = (e) => {
        const { scrollTop, scrollHeight, clientHeight } = e.target;
        if (scrollHeight - scrollTop - clientHeight < 50 && !loading && !loadingMore && rows.length < totalCount) {
            setOffset(prev => prev + 50);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="w-[95vw] max-w-[1200px] max-h-[90vh] flex flex-col gap-3 p-4 sm:p-5"
                data-testid="kpi-drilldown-dialog"
            >
                <DialogHeader className="space-y-1">
                    <DialogTitle data-testid="kpi-drilldown-title" className="text-base font-semibold">
                        {kpi?.title || "KPI"} <span className="text-muted-foreground font-normal text-sm">— underlying records</span>
                    </DialogTitle>
                    <DialogDescription className="text-xs">
                        {modelName ? `From ${modelName}. ` : ""}
                        {isSA
                            ? "All columns visible (super-admin view)."
                            : "Top contributing records for the current filter."}
                        {totalCount > rows.length && (
                            <span className="ml-1 font-mono opacity-70">
                                · {rows.length} of {totalCount.toLocaleString()}+
                            </span>
                        )}
                    </DialogDescription>
                </DialogHeader>

                <div className="flex-1 min-h-0 flex flex-col gap-2">
                    {loading && (
                        <div className="py-12 flex items-center justify-center text-muted-foreground text-sm">
                            <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading…
                        </div>
                    )}
                    {!loading && error && (
                        <div className="py-8 text-center text-sm text-muted-foreground" data-testid="kpi-drilldown-error">
                            {error}
                        </div>
                    )}
                    {!loading && !error && rows.length === 0 && (
                        <div className="py-8 text-center text-sm text-muted-foreground" data-testid="kpi-drilldown-empty">
                            No records for the current filter.
                        </div>
                    )}
                    {!loading && !error && rows.length > 0 && visibleColumns.length > 0 && (
                        <>
                            {/* Toggle bar: show all / less columns. Only for non-SA who get the curated subset by default. */}
                            {!isSA && hiddenCount > 0 && (
                                <div className="flex items-center justify-between text-xs">
                                    <button
                                        onClick={() => setShowAllCols((v) => !v)}
                                        className="inline-flex items-center gap-1 px-2 h-7 rounded-sm border border-border hover:bg-secondary/50 transition-colors font-mono uppercase tracking-wider text-[10px]"
                                        data-testid="kpi-drilldown-show-all"
                                    >
                                        {showAllCols
                                            ? (<><ChevronsLeft className="h-3 w-3" /> Show fewer</>)
                                            : (<><ChevronsRight className="h-3 w-3" /> Show {hiddenCount} more columns</>)}
                                    </button>
                                    <span className="text-muted-foreground font-mono text-[10px]">
                                        {visibleColumns.length} of {nonEmptyCols.length} cols
                                    </span>
                                </div>
                            )}
                            <div className="border border-border rounded-sm overflow-auto flex-1 min-h-0"
                                onScroll={handleScroll}
                                data-testid="kpi-drilldown-table">
                                <table className="text-sm w-max min-w-full">
                                    <thead className="bg-muted text-[10px] font-mono uppercase tracking-wider sticky top-0 z-20">
                                        <tr>
                                            {visibleColumns.map((c, ci) => (
                                                <th key={c}
                                                    className={`text-left px-3 py-2 whitespace-nowrap border-b border-border font-medium ${ci === 0 ? "sticky left-0 bg-muted z-30" : "bg-muted"}`}
                                                    title={c}>
                                                    {_humanize(c)}
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {rows.map((r, i) => (
                                            <tr key={i} className="border-t border-border/40 hover:bg-secondary/20 transition-colors"
                                                data-testid={`kpi-drilldown-row-${i}`}>
                                                {visibleColumns.map((c, ci) => (
                                                    <td key={c}
                                                        className={`px-3 py-2 whitespace-nowrap truncate max-w-[240px] tabular-nums ${ci === 0 ? "sticky left-0 bg-card z-10 font-medium" : ""}`}
                                                        title={String(r[c] ?? "")}>
                                                        {_fmtCell(r[c])}
                                                    </td>
                                                ))}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                {loadingMore && (
                                    <div className="py-4 flex justify-center text-muted-foreground">
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </div>

                <DialogFooter className="pt-1">
                    <Button variant="outline" size="sm" className="rounded-sm h-8" onClick={() => onOpenChange(false)}
                        data-testid="kpi-drilldown-close">Close</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
